import * as React from 'react';
import { Cross2Icon } from '@radix-ui/react-icons';
import * as ToastPrimitives from '@radix-ui/react-toast';
import { cva, type VariantProps } from 'class-variance-authority';

import { Icon } from '@eliadmin/core/src/components/svetech/ui/icon';
import { SVT_FOCUS_RING } from '@eliadmin/core/src/components/ui/svetech-shape';
import { cn } from '@eliadmin/core/src/utils/style-utils';

const ToastProvider = ToastPrimitives.Provider;

const ToastViewport = React.forwardRef<
  React.ElementRef<typeof ToastPrimitives.Viewport>,
  React.ComponentPropsWithoutRef<typeof ToastPrimitives.Viewport>
>(({ className, ...props }, ref) => (
  <ToastPrimitives.Viewport
    ref={ref}
    className={cn(
      'fixed left-0 right-0 top-0 z-[100] flex items-center justify-center px-0 sm:px-6 md:mx-auto md:max-w-[420px]',
      className,
    )}
    {...props}
  />
));
ToastViewport.displayName = ToastPrimitives.Viewport.displayName;

/*
 * SveTech alert (Key design „Upozornenie“): flat, radius 15 → 20 px with the square top-right
 * corner (full-bleed on phones), padding 16, 24 px icon; info #fdf4ce, success #eff6ed with a green
 * border, error #fdecea with a red border.
 */
const toastVariants = cva(
  'svt-body group pointer-events-auto relative flex w-full items-center justify-start space-x-2 overflow-hidden rounded-none border p-4 pr-8 text-[#14171a] transition-all [--svt-radius:15px] sm:rounded-[var(--svt-radius)] sm:rounded-tr-none lg:[--svt-radius:20px] data-[swipe=cancel]:translate-x-0 data-[swipe=end]:translate-x-[var(--radix-toast-swipe-end-x)] data-[swipe=move]:translate-x-[var(--radix-toast-swipe-move-x)] data-[swipe=move]:transition-none data-[state=open]:animate-in data-[state=closed]:animate-out data-[swipe=end]:animate-out data-[state=closed]:fade-out-80 data-[state=open]:slide-in-from-top-full data-[state=open]:sm:slide-in-from-bottom-full',
  {
    variants: {
      variant: {
        default: 'border-transparent bg-[#fdf4ce]',
        success: 'success group border-[#2c7a25] bg-[#eff6ed]',
        destructive: 'destructive group border-[#b3261e] bg-[#fdecea]',
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  },
);

const Toast = React.forwardRef<
  React.ElementRef<typeof ToastPrimitives.Root>,
  React.ComponentPropsWithoutRef<typeof ToastPrimitives.Root> &
    VariantProps<typeof toastVariants> & { icon?: React.ReactNode }
>(({ className, variant, icon, ...props }, ref) => {
  const getIcon = () => {
    if (icon) return icon;
    switch (variant) {
      case 'success':
        return <Icon name="seal-check" className="mr-2 size-6 shrink-0 text-[#2c7a25]" />;
      case 'destructive':
        return <Icon name="warning-circle" className="mr-2 size-6 shrink-0 text-[#b3261e]" />;
    }
  };

  return (
    <ToastPrimitives.Root
      ref={ref}
      className={cn(toastVariants({ variant }), className)}
      {...props}
    >
      {getIcon()}
      {props.children}
    </ToastPrimitives.Root>
  );
});
Toast.displayName = ToastPrimitives.Root.displayName;

const ToastAction = React.forwardRef<
  React.ElementRef<typeof ToastPrimitives.Action>,
  React.ComponentPropsWithoutRef<typeof ToastPrimitives.Action>
>(({ className, ...props }, ref) => (
  <ToastPrimitives.Action
    ref={ref}
    className={cn(
      'svt-button inline-flex h-8 shrink-0 items-center justify-center rounded-[8px] rounded-tr-none border border-[#dfe2db] bg-transparent px-3 transition-colors hover:bg-white disabled:pointer-events-none disabled:opacity-70',
      SVT_FOCUS_RING,
      className,
    )}
    {...props}
  />
));
ToastAction.displayName = ToastPrimitives.Action.displayName;

const ToastClose = React.forwardRef<
  React.ElementRef<typeof ToastPrimitives.Close>,
  React.ComponentPropsWithoutRef<typeof ToastPrimitives.Close>
>(({ className, ...props }, ref) => (
  <ToastPrimitives.Close
    ref={ref}
    className={cn(
      'absolute right-1 top-1 rounded p-1 text-[#6b7681] opacity-0 transition-opacity hover:text-[#14171a] focus:opacity-100 group-hover:opacity-100',
      SVT_FOCUS_RING,
      className,
    )}
    toast-close=""
    {...props}
  >
    <Cross2Icon className="h-4 w-4" />
  </ToastPrimitives.Close>
));
ToastClose.displayName = ToastPrimitives.Close.displayName;

const ToastTitle = React.forwardRef<
  React.ElementRef<typeof ToastPrimitives.Title>,
  React.ComponentPropsWithoutRef<typeof ToastPrimitives.Title>
>(({ className, ...props }, ref) => (
  <ToastPrimitives.Title ref={ref} className={cn('font-bold', className)} {...props} />
));
ToastTitle.displayName = ToastPrimitives.Title.displayName;

const ToastDescription = React.forwardRef<
  React.ElementRef<typeof ToastPrimitives.Description>,
  React.ComponentPropsWithoutRef<typeof ToastPrimitives.Description> & {
    icon?: React.ReactNode;
  }
>(({ className, ...props }, ref) => (
  <ToastPrimitives.Description
    ref={ref}
    className={cn('whitespace-pre-wrap', className)}
    {...props}
  />
));
ToastDescription.displayName = ToastPrimitives.Description.displayName;

type ToastProps = React.ComponentPropsWithoutRef<typeof Toast>;

type ToastActionElement = React.ReactElement<typeof ToastAction>;

export {
  type ToastProps,
  type ToastActionElement,
  ToastProvider,
  ToastViewport,
  Toast,
  ToastTitle,
  ToastDescription,
  ToastClose,
  ToastAction,
};
